'use client';

import { SpinnerIcon } from '@app/app/_icons/SpinnerIcon';
import Link from 'next/link';
import { UrlObject } from 'url';
import { ReactNode, useCallback } from 'react';
import { useFormStatus } from 'react-dom';
import { twJoin, twMerge } from 'tailwind-merge';

interface Props {
    variant?: 'primary' | 'outlined' | 'text';
    color?: 'primary' | 'neutral' | 'danger';
    size?: 'normal' | 'small' | 'tiny';
    href?: string | UrlObject;
    isLoading?: boolean;
    onClick?: () => void;
    form?: string;
    tooltip?: string;
    disabled?: boolean;
    className?: string;
    leftAddon?: ReactNode;
    rightAddon?: ReactNode;
    children: ReactNode;
    target?: string;
    preventDefault?: boolean;
    ignoreFormStatus?: boolean;
    useAnchorTag?: boolean;
}

export function Button({
    variant = 'primary',
    color = 'primary',
    size = 'normal',
    onClick,
    href,
    form,
    tooltip,
    disabled = false,
    className,
    leftAddon,
    rightAddon,
    children,
    target,
    isLoading = false,
    preventDefault = false,
    ignoreFormStatus = false,
    useAnchorTag = false,
}: Props) {
    const { pending } = useFormStatus();

    const effectivePending = ignoreFormStatus ? false : pending;

    const handleClick = useCallback(
        (e: React.MouseEvent<HTMLElement>) => {
            e.preventDefault();
            e.stopPropagation();

            if (onClick) {
                onClick();
            }
        },
        [onClick],
    );

    const classNames = twMerge(
        'relative flex justify-center items-center disabled:pointer-events-none data-[disabled]:pointer-events-none',
        'rounded-full font-medium transition-colors outline-offset-2',
        'disabled:text-black/25 disabled:bg-neutral-100 disabled:border-neutral-100',
        'data-[disabled]:text-black/25 data-[disabled]:bg-neutral-100 data-[disabled]:border-neutral-100',
        size === 'normal' && 'min-h-[3.25rem] px-6',
        size === 'small' && 'min-h-[2.5rem] px-5',
        size === 'tiny' && 'min-h-[2.5rem] md:min-h-[2.125rem] px-4 text-sm',
        variant === 'primary' &&
            color === 'primary' &&
            'bg-primary-500 text-white active:bg-primary-500-hover md:active:bg-primary-500 md:hover:bg-primary-500-hover',
        variant === 'primary' &&
            color === 'neutral' &&
            'bg-neutral-700 text-white active:bg-neutral-700-hover md:active:bg-neutral-700 md:hover:bg-neutral-700-hover',
        variant === 'primary' &&
            color === 'danger' &&
            'bg-red-500 text-white active:bg-red-500-hover md:active:bg-red-500 md:hover:bg-red-500-hover',
        variant === 'outlined' &&
            color === 'primary' &&
            'border border-primary-500 bg-white text-primary-500 active:bg-primary-100 md:active:bg-white md:hover:bg-primary-100',
        variant === 'outlined' &&
            color === 'neutral' &&
            'border border-neutral-300 bg-white text-neutral-700 active:bg-neutral-50 md:active:bg-white md:hover:bg-neutral-50',
        variant === 'outlined' &&
            color === 'danger' &&
            'border border-red-500 bg-white text-red-500 active:bg-red-50 md:active:bg-white md:hover:bg-red-50',
        variant === 'text' &&
            color === 'primary' &&
            'text-primary-500 p-0 disabled:bg-transparent disabled:border-none',
        className,
    );

    if (href != null && !useAnchorTag) {
        return (
            <Link
                className={classNames}
                href={href}
                target={target}
                title={tooltip}
                data-disabled={disabled || undefined}
                onClick={onClick}>
                {renderChildren()}
            </Link>
        );
    }

    if (href != null && useAnchorTag) {
        return (
            <a href={href.toString()} className={classNames} target={target} title={tooltip} onClick={onClick}>
                {renderChildren()}
            </a>
        );
    }

    return (
        <button
            className={classNames}
            type={onClick == null ? 'submit' : 'button'}
            onClick={preventDefault ? handleClick : onClick}
            disabled={disabled || effectivePending || isLoading}
            form={form}
            title={tooltip}>
            {(effectivePending || isLoading) && (
                <span className="absolute">
                    <SpinnerIcon
                        className={twJoin(
                            'animate-spin text-primary-500',
                            size === 'normal' && 'size-5',
                            size === 'small' && 'size-4',
                        )}
                    />
                </span>
            )}
            {renderChildren(effectivePending || isLoading)}
        </button>
    );

    function renderChildren(invisible = false) {
        return (
            <span className={twJoin('inline-flex items-center gap-2', invisible && 'invisible')}>
                {leftAddon != null && leftAddon}
                <span>{children}</span>
                {rightAddon != null && rightAddon}
            </span>
        );
    }
}
